import {
  cloneElement,
  isValidElement,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react'
import { Link } from 'react-router'
import { CarouselButtonNext } from '../carousel-button-next/carousel-button-next'
import { LinkArrow } from '../link-arrow/link-arrow'

export interface TabAccordionItem {
  id: string
  label: string
  description: ReactNode
  screenshot?: string
  screenshotAlt?: string
  screenshotContent?: ReactNode
  screenshotLabel?: string
  screenshotAspectRatio?: string
  noBorder?: boolean
}

interface TabAccordionProps {
  name: string
  id?: string
  heading: ReactNode
  subheading?: ReactNode
  items: TabAccordionItem[]
  className?: string
  imagePosition?: 'left' | 'right'
  /** Remove borders from all screenshots (panel + mobile inline) */
  noBorder?: boolean
  showNavArrows?: boolean
  navArrowsVariant?: 'default' | 'carousel'
  fullWidthHeading?: boolean
}

type ElementWithChildren = ReactElement<{
  ariaLabel?: string
  children?: ReactNode
  className?: string
  external?: boolean
  href?: string
}>

const SCREENSHOT_MAX_HEIGHT = 521
const SCREENSHOT_MAX_WIDTH = 868

const parseAspectRatio = (ratio: string): number | undefined => {
  const [width, height] = ratio.split('/').map((part) => Number(part.trim()))

  if (!width || !height) return undefined

  return width / height
}

const renderAccordionCtas = (node: ReactNode): ReactNode => {
  if (Array.isArray(node)) {
    return node.map((child) => renderAccordionCtas(child))
  }

  if (!isValidElement(node)) {
    return node
  }

  const element = node as ElementWithChildren

  if (element.type === LinkArrow && element.props.href) {
    const content = renderAccordionCtas(element.props.children)

    if (element.props.external) {
      return (
        <span className="tab-accordion-description-cta">
          <a
            aria-label={element.props.ariaLabel}
            className="call-to-action primary"
            href={element.props.href}
            rel="noopener noreferrer"
            target="_blank"
          >
            {content}
          </a>
        </span>
      )
    }

    return (
      <span className="tab-accordion-description-cta">
        <Link
          aria-label={element.props.ariaLabel}
          className="call-to-action primary"
          to={element.props.href}
        >
          {content}
        </Link>
      </span>
    )
  }

  return cloneElement(element, {
    children:
      element.props.children === undefined
        ? element.props.children
        : renderAccordionCtas(element.props.children),
  })
}

export const TabAccordion = ({
  name,
  id,
  heading,
  subheading,
  items,
  className,
  imagePosition = 'right',
  noBorder,
  showNavArrows,
  navArrowsVariant = 'default',
  fullWidthHeading,
}: TabAccordionProps) => {
  const [activeId, setActiveId] = useState(items[0]?.id ?? '')
  const activeIndex = items.findIndex((item) => item.id === activeId)

  const stepToItem = (offset: number): void => {
    if (items.length === 0) return

    const nextIndex = (activeIndex + offset + items.length) % items.length
    const nextItem = items[nextIndex]
    document.getElementById(`${name}-${nextItem.id}`)?.click()
  }
  const [screenshotRatios, setScreenshotRatios] = useState<
    Record<string, string>
  >({})
  const wrapperClass = `tab-accordion-wrapper tab-accordion-${name} tab-accordion-image-${imagePosition}${fullWidthHeading ? ' tab-accordion-full-width-heading' : ''}`
  const activeItem = items.find((item) => item.id === activeId)
  const activeScreenshotRatio =
    activeItem?.screenshotAspectRatio ?? screenshotRatios[activeId]
  const activeScreenshotRatioValue = activeScreenshotRatio
    ? parseAspectRatio(activeScreenshotRatio)
    : undefined
  const activeScreenshotMaxWidth = activeScreenshotRatioValue
    ? Math.min(
        SCREENSHOT_MAX_WIDTH,
        Math.round(SCREENSHOT_MAX_HEIGHT * activeScreenshotRatioValue),
      )
    : undefined
  const wrapperStyle = activeScreenshotRatio
    ? ({
        '--tab-accordion-screenshot-aspect-ratio': activeScreenshotRatio,
        ...(activeScreenshotMaxWidth
          ? {
              '--tab-accordion-screenshot-max-width': `${activeScreenshotMaxWidth}px`,
            }
          : {}),
      } as CSSProperties)
    : undefined

  const setScreenshotRatio = (
    itemId: string,
    image: HTMLImageElement,
  ): void => {
    if (!image.naturalWidth || !image.naturalHeight) return

    const ratio = `${image.naturalWidth} / ${image.naturalHeight}`

    setScreenshotRatios((currentRatios) =>
      currentRatios[itemId] === ratio
        ? currentRatios
        : { ...currentRatios, [itemId]: ratio },
    )
  }

  const getScreenshotStyle = (
    item: TabAccordionItem,
  ): CSSProperties | undefined => {
    const screenshotRatio =
      item.screenshotAspectRatio ?? screenshotRatios[item.id]

    return screenshotRatio
      ? ({
          '--tab-accordion-screenshot-aspect-ratio': screenshotRatio,
        } as CSSProperties)
      : undefined
  }

  const renderScreenshot = (item: TabAccordionItem): ReactNode => {
    if (item.screenshotContent) {
      return (
        <div
          className="tab-accordion-screenshot-media"
          role="img"
          aria-label={item.screenshotLabel ?? item.screenshotAlt}
          style={getScreenshotStyle(item)}
        >
          {item.screenshotContent}
        </div>
      )
    }

    if (!item.screenshot) return null

    return (
      <img
        className="tab-accordion-screenshot-media"
        loading="lazy"
        src={item.screenshot}
        alt={item.screenshotAlt ?? ''}
        style={getScreenshotStyle(item)}
        onLoad={(event) => setScreenshotRatio(item.id, event.currentTarget)}
      />
    )
  }

  const noBorderStyles = noBorder
    ? `.tab-accordion-${name} .tab-accordion-screenshots { border: none; }
       .tab-accordion-${name} .tab-accordion-screenshot-inline .tab-accordion-screenshot-media { border: none; box-shadow: none; }`
    : ''

  const instanceStyles =
    noBorderStyles +
    items
      .map(
        (item) => `
        .tab-accordion-${name}:has(input[value="${item.id}"]:checked)
          .tab-accordion-screenshots .tab-accordion-screenshot[data-id="${item.id}"] {
          opacity: 1;
          z-index: 1;
        }
        .tab-accordion-${name}:has(input[value="${item.id}"]:checked)
          .tab-accordion-item[data-id="${item.id}"] {
          background: var(--color-light-gray-next);
        }
        .tab-accordion-${name}:has(input[value="${item.id}"]:checked)
          .tab-accordion-item[data-id="${item.id}"] label {
          background: var(--color-light-gray-next);
        }
        .tab-accordion-${name}:has(input[value="${item.id}"]:checked)
          .tab-accordion-item[data-id="${item.id}"] .tab-accordion-icon {
          background: var(--color-brand-primary-next);
        }
        .tab-accordion-${name}:has(input[value="${item.id}"]:checked)
          .tab-accordion-item[data-id="${item.id}"] .tab-accordion-icon svg {
          color: #fff;
          transform: rotate(90deg);
        }
        .tab-accordion-${name}:has(input[value="${item.id}"]:checked)
          .tab-accordion-item[data-id="${item.id}"] .tab-accordion-panel {
          grid-template-rows: 1fr;
          opacity: 1;
          transition: grid-template-rows 0.35s ease, opacity 0.2s ease 0.08s;
        }
        .tab-accordion-${name}:has(input[value="${item.id}"]:focus-visible)
          .tab-accordion-item[data-id="${item.id}"] label {
          outline: 2px solid var(--color-brand);
          outline-offset: 2px;
        }
        ${
          item.noBorder
            ? `.tab-accordion-${name}:has(input[value="${item.id}"]:checked) .tab-accordion-screenshots {
          border: none;
        }
        .tab-accordion-${name}:has(input[value="${item.id}"]:checked) .tab-accordion-item[data-id="${item.id}"] .tab-accordion-screenshot-inline .tab-accordion-screenshot-media {
          border: none;
          box-shadow: none;
        }`
            : ''
        }
      `,
      )
      .join('')

  return (
    <section id={id} className={`tab-accordion-section ${className ?? ''}`}>
      <style dangerouslySetInnerHTML={{ __html: instanceStyles }} />

      <fieldset
        className={wrapperClass}
        style={wrapperStyle}
        aria-label={`Select a ${name} option`}
      >
        {fullWidthHeading ? (
          <hgroup className="tab-accordion-full-width-hgroup">
            <div className="text-combo">
              <h2 className="heading-xxl text-brand-dark">{heading}</h2>
              {subheading ? (
                <p className="body-l text-primary">{subheading}</p>
              ) : null}
            </div>
          </hgroup>
        ) : null}

        <div className="tab-accordion-layout">
          <div className="tab-accordion-content">
            {fullWidthHeading ? null : (
              <hgroup className="">
                <div className="text-combo">
                  <h2 className="heading-xl text-brand-dark">{heading}</h2>
                  {subheading ? (
                    <p className="body-l text-primary">{subheading}</p>
                  ) : null}
                </div>
              </hgroup>
            )}

            <div className="tab-accordion-tabs">
              {items.map((item, index) => (
                <div
                  key={item.id}
                  className="tab-accordion-item"
                  data-id={item.id}
                >
                  <label htmlFor={`${name}-${item.id}`}>
                    <input
                      type="radio"
                      id={`${name}-${item.id}`}
                      name={name}
                      value={item.id}
                      defaultChecked={index === 0}
                      onChange={() => setActiveId(item.id)}
                    />
                    <span className="tab-accordion-icon" aria-hidden="true">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="5"
                        height="8"
                        viewBox="0 0 5 8"
                        fill="none"
                      >
                        <path
                          d="M0.571289 0.571289L3.93026 3.93018L0.571289 7.28916"
                          stroke="currentColor"
                          strokeWidth="1.14258"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                    <span className="body-m text-brand-dark">{item.label}</span>
                  </label>
                  <div className="tab-accordion-panel">
                    <div className="tab-accordion-panel-inner">
                      <div className="tab-accordion-description">
                        <p className="body-m text-brand-dark">
                          {renderAccordionCtas(item.description)}
                        </p>
                      </div>
                      <div className="tab-accordion-screenshot-inline">
                        {renderScreenshot(item)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="tab-accordion-screenshots">
            {items.map((item) => (
              <div
                key={item.id}
                className="tab-accordion-screenshot"
                data-id={item.id}
                style={getScreenshotStyle(item)}
              >
                {renderScreenshot(item)}
              </div>
            ))}
          </div>

          {showNavArrows && items.length > 1 ? (
            navArrowsVariant === 'carousel' ? (
              <div className="tab-accordion-nav-arrows carousel-controls-next">
                <CarouselButtonNext
                  direction="previous"
                  aria-label={`Previous ${name} option`}
                  onClick={() => stepToItem(-1)}
                />
                <CarouselButtonNext
                  direction="next"
                  aria-label={`Next ${name} option`}
                  onClick={() => stepToItem(1)}
                />
              </div>
            ) : (
              <div className="tab-accordion-nav-arrows">
                <button
                  type="button"
                  aria-label={`Previous ${name} option`}
                  onClick={() => stepToItem(-1)}
                >
                  <svg viewBox="0 0 20 16" fill="none" aria-hidden="true">
                    <path
                      d="M19 8H1M1 8L8 1M1 8L8 15"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  aria-label={`Next ${name} option`}
                  onClick={() => stepToItem(1)}
                >
                  <svg viewBox="0 0 20 16" fill="none" aria-hidden="true">
                    <path
                      d="M1 8H19M19 8L12 1M19 8L12 15"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )
          ) : null}
        </div>
      </fieldset>
    </section>
  )
}
